import { useState, useMemo } from "react";

import { allTrackers } from "app/services/track/track";

import type { IFoldersItem } from "../../../types";

export const useTableExpansion = (
    data: IFoldersItem[],
    hasSearch: boolean,
    filters?: { isNew?: boolean; isTrending?: boolean },
) => {
    const [userExpandedKeys, setUserExpandedKeys] = useState<string[]>([]);
    const [userCollapsedKeys, setUserCollapsedKeys] = useState<string[]>([]);

    const searchExpandedKeys = useMemo(() => {
        if (!hasSearch) {
            return [];
        }

        const expandedKeys: string[] = [];
        const findExpandableItems = (items: IFoldersItem[]) => {
            for (const item of items) {
                if (item.shouldExpand && item.key) {
                    expandedKeys.push(item.key);
                }
                if (item.children) {
                    findExpandableItems(item.children);
                }
            }
        };
        findExpandableItems(data);
        return expandedKeys;
    }, [hasSearch, data]);

    const filterExpandedKeys = useMemo(() => {
        if (filters?.isNew || filters?.isTrending) {
            const expandedKeys: string[] = [];
            const findAllParents = (items: IFoldersItem[]) => {
                for (const item of items) {
                    if (item.children && item.children.length > 0 && item.key) {
                        expandedKeys.push(item.key);
                        findAllParents(item.children);
                    }
                }
            };
            findAllParents(data);
            return expandedKeys;
        }
        return [];
    }, [filters?.isNew, filters?.isTrending, data]);

    const allExpandedKeys = useMemo(() => {
        const automaticKeys = [...new Set([...searchExpandedKeys, ...filterExpandedKeys])];

        const finalKeys = [...automaticKeys, ...userExpandedKeys].filter((key) => !userCollapsedKeys.includes(key));

        return [...new Set(finalKeys)];
    }, [searchExpandedKeys, filterExpandedKeys, userExpandedKeys, userCollapsedKeys]);

    const onExpandedRowsChange = (keys: readonly React.Key[]) => {
        const newExpandedKeys = keys as string[];

        // Get all automatic expansions (search + filter)
        const automaticExpandedKeys = [...new Set([...searchExpandedKeys, ...filterExpandedKeys])];

        const userChanges = newExpandedKeys.filter((key) => !automaticExpandedKeys.includes(key));
        const userRemovals = automaticExpandedKeys.filter((key) => !newExpandedKeys.includes(key));

        // Update states efficiently
        setUserExpandedKeys(userChanges);
        setUserCollapsedKeys(userRemovals);
    };

    const isRowExpandable = (record: IFoldersItem) => Boolean(record.children?.length);

    const handleExpand = (expanded: boolean, record: IFoldersItem) => {
        const directionString = expanded ? "Expand" : "Collapse";
        allTrackers.trackEvent("Expand Collapse", "click", `Table/${directionString}/${record.folder}`);
    };

    return {
        expandedRowKeys: allExpandedKeys,
        onExpandedRowsChange,
        rowExpandable: isRowExpandable,
        onExpand: handleExpand,
    };
};
